import React from 'react';
import { Document, Page, Text, View, StyleSheet, Link, Font, Image } from '@react-pdf/renderer';

Font.register({
  family: 'Computer Modern',
  fonts: [
    { src: '/Computer Modern_2.ttf' },
    { src: '/Computer Modern_3.ttf', fontWeight: 'bold' },
    { src: '/times-new-roman-italic.ttf', fontStyle: 'italic' },
  ],
});

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 12,
    fontFamily: 'Computer Modern',
    fontWeight: 'normal'
  },
  header: {
    fontSize: 26,
    textAlign: 'center',
    marginBottom: 5,
  },
  summary: {
    color: '#fffff',
    fontWeight: 'normal',
  },
  contactDetails: {
    textAlign: 'center',
    marginBottom: 10,
    fontSize: 10,
  },
  section: {
    marginBottom: 15,
  },
  heading: {
    fontSize: 14,
    marginBottom: 10,
    textTransform: 'uppercase',
    borderBottom: '1px solid black',
    fontWeight: 'bold',
    color: '#fffff',
  },
  headingNoLine: {
    fontSize: 14,
    marginBottom: 10,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    color: '#fffff',
  },
  link: {
    color: '#fffff',
    textDecoration: 'none',
  },
  spaceBetween: {
    marginRight: 10,
  },
  experience: {
    marginBottom: 10,
  },
  skillContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  skill: {

  },
  sts1: {
    fontSize: 12,
    marginVertical: 2,
    width: '100%',
    flexDirection: 'row',
  },
  sts2: {
    fontWeight: 'bold',
    fontSize: 12,
    width: '30%',
  },
  sts3: {
    fontSize: 12,
    width: '70%',
    paddingLeft: 5,
  },
  childTitle: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  icon: {
    width: 8,
    height: 7,
    marginRight: 5,
  },
  expMain: {
    marginVertical: 2,
    width: '100%',
    flexDirection: 'row',
  },
  expSub1: {
    fontWeight: 'bold',
    width: '2%',
  },
  expSub2: {
    width: '98%',
    paddingLeft: 5,
  },
  proMain: {
    marginVertical: 2,
    width: '100%',
    flexDirection: 'row',
  },
  proSub1: {
    fontWeight: 'bold',
    width: '2%',
    marginLeft: 10,
  },
  proSub2: {
    width: '98%',
    paddingLeft: 5,
  },
});

// Helper function to check if section has valid content
const hasContent = (value) => {
  if (!value) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

// Helper function to check if skills section has valid data
const hasValidSkills = (skills) => {
  if (!skills || skills.length === 0) return false;
  return skills.some(skill => 
    skill.title?.trim().length > 0 || 
    (skill.items && skill.items.some(item => item?.trim().length > 0))
  );
};

// Helper function to check if experiences have valid data
const hasValidExperiences = (experiences) => {
  if (!experiences || experiences.length === 0) return false;
  return experiences.some(exp => 
    exp.designation?.trim().length > 0 || 
    exp.company?.trim().length > 0 ||
    (exp.workSummary && exp.workSummary.some(item => item?.trim().length > 0))
  );
};

// Helper function to check if projects have valid data
const hasValidProjects = (projects) => {
  if (!projects || projects.length === 0) return false;
  return projects.some(project => 
    project.name?.trim().length > 0 || 
    (project.summary && project.summary.some(item => item?.trim().length > 0))
  );
};

// Helper function to check if education has valid data
const hasValidEducation = (education) => {
  if (!education || education.length === 0) return false;
  return education.some(edu => 
    edu.name?.trim().length > 0 || 
    edu.college?.trim().length > 0
  );
};

// Helper function to check if certifications have valid data
const hasValidCertifications = (certifications) => {
  if (!certifications || certifications.length === 0) return false;
  return certifications.some(cert => 
    cert.name?.trim().length > 0 || 
    cert.institution?.trim().length > 0
  );
};

const ResumePDF = ({ formData }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.header}>{formData.name}</Text>
      <View style={styles.contactDetails}>
        {formData.mobile && formData.address && formData.mobile.length > 1 && (
          <Text style={{ marginBottom: 10 }}>
            <Image style={styles.icon} src="/phone.png" />{' '}
            {formData.mobile}
            {' '}<Image style={styles.icon} src="/location.png" />{' '}
            {formData.address}
          </Text>
        )}
        <Text>
          {/* Email */}
          {formData.email && formData.email.length > 1 && (
            <Text style={styles.spaceBetween}>
              <Image style={styles.icon} src="/mail.png" />{' '}
              <Link style={styles.link} src={`mailto:${formData.email}`}>{formData.email}</Link>
            </Text>
          )}
          {' '}
          {formData.link1 && formData.link1.length > 1 && (
            <Text style={styles.spaceBetween}>
              {' '} <Image style={styles.icon} src="/linkedin.png" />{' '}
              <Link style={styles.link} src={formData.link1}>{formData.link1}</Link>
            </Text>
          )}
          {formData.link2 && formData.link2.length > 1 && (
            <Text style={styles.spaceBetween}>
              {' '} <Image style={styles.icon} src="/link.png" />{' '}
              <Link style={styles.link} src={formData.link2}>{formData.link2}</Link>
            </Text>
          )}
        </Text>
      </View>

      {/* Objective/Summary Section - Only show if has content */}
      {hasContent(formData.objective) && (
        <View style={styles.section}>
          <Text style={styles.heading}>Summary</Text>
          <Text>{formData.objective}</Text>
        </View>
      )}

      {/* Skills Section - Only show if has valid skills */}
      {hasValidSkills(formData.skills) && (
        <View style={styles.section}>
          <Text style={styles.heading}>Skills</Text>
          <View style={styles.skillContainer}>
            {formData.skills.map((skill, index) => (
              <View key={index} style={styles.skill}>
                {skill.items && skill.items.map((item, idx) => (
                  item && item.trim().length > 0 && (
                    <View style={styles.sts1} key={idx}>
                      <View style={styles.sts2}>
                        {skill.title && skill.title.trim().length > 0 && (
                          <Text style={styles.childTitle}>{skill.title}</Text>
                        )}
                      </View>
                      <View style={styles.sts3}>
                        <Text>{item}</Text>
                      </View>
                    </View>
                  )
                ))}
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Experience Section - Only show if has valid experiences */}
      {hasValidExperiences(formData.experiences) && (
        <View style={styles.section}>
          <Text style={styles.heading}>Experience</Text>
          {formData.experiences.map((experience, index) => (
            (experience.designation?.trim().length > 0 || experience.company?.trim().length > 0) && (
              <View key={index}>
                <Text style={styles.childTitle}>
                  {experience.designation && experience.designation.trim().length > 0 && experience.designation}
                  {experience.designation && experience.company && experience.designation.trim().length > 0 && experience.company.trim().length > 0 && ' - '}
                  {experience.company && experience.company.trim().length > 0 && experience.company}
                </Text>

                {(experience.startDate?.trim().length > 0 || experience.endDate?.trim().length > 0) && (
                  <Text>
                    {experience.startDate && experience.startDate.trim().length > 0 && experience.startDate}
                    {experience.startDate && experience.endDate && experience.startDate.trim().length > 0 && experience.endDate.trim().length > 0 && ' - '}
                    {experience.endDate && experience.endDate.trim().length > 0 && experience.endDate}
                  </Text>
                )}

                {experience.workSummary && experience.workSummary.length > 0 && (
                  <View style={{ marginLeft: 10 }}>
                    {experience.workSummary.map((point, idx) => (
                      point && point.trim().length > 0 && (
                        <View key={idx} style={styles.expMain}>
                          <View style={styles.expSub1}>
                            <Text style={{ fontWeight: 'bold', fontSize: 12 }}>•</Text>
                          </View>
                          <View style={styles.expSub2}>
                            <Text style={{ fontWeight: 'normal', fontFamily: 'Computer Modern' }}>
                              {point}
                            </Text>
                          </View>
                        </View>
                      )
                    ))}
                  </View>
                )}
              </View>
            )
          ))}
        </View>
      )}

      {/* Projects Section - Only show if has valid projects */}
      {hasValidProjects(formData.projects) && (
        <View style={styles.section}>
          <Text style={styles.heading}>Projects</Text>
          {formData.projects.map((project, index) => (
            (project.name?.trim().length > 0 || (project.summary && project.summary.some(s => s?.trim().length > 0))) && (
              <View key={index}>
                {project.name && project.name.trim().length > 0 && (
                  <Text style={styles.childTitle}>{project.name}</Text>
                )}

                {project.summary && project.summary.length > 0 && (
                  project.summary.map((point, idx) => (
                    point && point.trim().length > 0 && (
                      <View key={idx} style={styles.proMain}>
                        <View style={styles.proSub1}>
                          <Text style={{ fontWeight: 'bold', fontSize: 12 }}>•</Text>
                        </View>
                        <View style={styles.proSub2}>
                          <Text style={{ fontWeight: 'normal', fontFamily: 'Computer Modern' }}>
                            {point}
                          </Text>
                        </View>
                      </View>
                    )
                  ))
                )}
              </View>
            )
          ))}
        </View>
      )}

      {/* Education Section - Only show if has valid education */}
      {hasValidEducation(formData.education) && (
        <View style={styles.section}>
          <Text style={styles.heading}>Education</Text>
          {formData.education.map((edu, index) => (
            (edu.name?.trim().length > 0 || edu.college?.trim().length > 0) && (
              <View key={index}>
                <Text style={styles.childTitle}>
                  {edu.name && edu.name.trim().length > 0 && edu.name}
                  {edu.name && edu.college && edu.name.trim().length > 0 && edu.college.trim().length > 0 && ' - '}
                  {edu.college && edu.college.trim().length > 0 && edu.college}
                </Text>
                {(edu.startDate?.trim().length > 0 || edu.endDate?.trim().length > 0) && (
                  <Text>
                    {edu.startDate && edu.startDate.trim().length > 0 && edu.startDate}
                    {edu.startDate && edu.endDate && edu.startDate.trim().length > 0 && edu.endDate.trim().length > 0 && ' - '}
                    {edu.endDate && edu.endDate.trim().length > 0 && edu.endDate}
                  </Text>
                )}
              </View>
            )
          ))}
        </View>
      )}

      {/* Certifications Section - Only show if has valid certifications */}
      {hasValidCertifications(formData.certifications) && (
        <View style={styles.section}>
          <Text style={styles.heading}>Certifications</Text>
          {formData.certifications.map((certification, index) => (
            (certification.name?.trim().length > 0 || certification.institution?.trim().length > 0) && (
              <View key={index}>
                <Text style={styles.childTitle}>
                  {certification.name && certification.name.trim().length > 0 && certification.name}
                  {certification.name && certification.institution && certification.name.trim().length > 0 && certification.institution.trim().length > 0 && ' - '}
                  {certification.institution && certification.institution.trim().length > 0 && certification.institution}
                </Text>
                {(certification.startDate?.trim().length > 0 || certification.endDate?.trim().length > 0) && (
                  <Text>
                    {certification.startDate && certification.startDate.trim().length > 0 && certification.startDate}
                    {certification.startDate && certification.endDate && certification.startDate.trim().length > 0 && certification.endDate.trim().length > 0 && ' - '}
                    {certification.endDate && certification.endDate.trim().length > 0 && certification.endDate}
                  </Text>
                )}
              </View>
            )
          ))}
        </View>
      )}
    </Page>
  </Document>
);

export default ResumePDF;